import { t } from "i18next";
import { useMemo } from "react";
import { CompanyData } from "../../../to-be-removed/LocalData/Company/CompanyData";
import { LocalData } from "../../../to-be-removed/LocalData/General/LocalData";
import { PropertyData } from "../../../to-be-removed/LocalData/Property/PropertyData";
import { SelectInput } from "../../ui-components/Selectors/SelectInput/SelectInput";
import { useProperties } from "@src/data-hooks/property/property.service";
import { IRawProperty } from "@propely/propely-typings";
import { SelectInputOption } from "@src/components/ui-components/Selectors/SelectInput/SelectInput.types";

interface PropertySelectorProps {
  companies?: CompanyData[];
  module?: "spreadsheet" | "deviations" | "routines" | "contractors" | "surveys" | "chats" | "fire" | "digitalKeys" | "incomeOverview" | "booking";
  properties?: PropertyData[];
  isMulti: boolean;
  width?: string;
  defaultSelected?: PropertyData[];
  /**
   * @TODO rename to 'value' and have dynamic type for string or string[] depending on isMulti (like in SelectInput)
   **/
  optionsSelected?: PropertyData[] | undefined;
  /**
   * @deprecated use 'changedSelectedProperties' instead
   **/
  changedSelected?: (e: any) => void;
  /**
   * @TODO rename to 'onChange' and have dynamic type for string or string[] depending on isMulti (like in SelectInput)
   **/
  changedSelectedProperties?: (properties: PropertyData[]) => void;
  placeholder?: string;
  className?: string;
  selectAll?: boolean;
  defaultMenuIsOpen?: boolean;
  dataCy?: string;
  disabled?: boolean;
  disableSelectAll?: boolean;
}

export const PropertySelector = (props: PropertySelectorProps) => {
  const propertyIds = useMemo(() => {
    if (props.properties) {
      return props.properties.map((property) => property.id);
    }
    if (props.companies) {
      return props.companies.flatMap((company) => company.properties.map((property) => property.id));
    }
    return [];
  }, [props.properties, props.companies]);

  const { properties } = useProperties(propertyIds);

  const propertyOptions: SelectInputOption[] = useMemo(() => {
    if (!properties) return [];
    return properties
      .map((property) => ({
        value: property.id,
        label: property.name ?? "",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [properties]);

  const changedSelected = (selectedOptions: readonly string[] | string | null) => {
    if (props.changedSelectedProperties) {
      let selectedProperties: IRawProperty[] = [];
      if (Array.isArray(selectedOptions)) {
        selectedProperties = selectedOptions
          .map((id) => properties.find((property) => property.id === id))
          .filter((property) => property !== undefined);
      } else if (selectedOptions && typeof selectedOptions === "string") {
        const selectedProperty = properties.find((property) => property.id === selectedOptions);
        if (selectedProperty) {
          selectedProperties.push(selectedProperty);
        }
      }

      // Map IRawProperty to PropertyData
      const selectedPropertyDatas: PropertyData[] = selectedProperties.map((property) => LocalData.properties.addData(property.id));
      props.changedSelectedProperties(selectedPropertyDatas);
    } else if (props.changedSelected) {
      props.changedSelected(selectedOptions);
    }
  };

  const selectedValue = useMemo(() => {
    if (props.optionsSelected) {
      return props.isMulti ? props.optionsSelected.map((p) => p.id) : (props.optionsSelected[0]?.id ?? null);
    } else if (props.defaultSelected) {
      return props.isMulti ? props.defaultSelected.map((p) => p.id) : (props.defaultSelected[0]?.id ?? null);
    } else if (props.selectAll) {
      return props.isMulti ? propertyOptions.map((o) => o.value) : null;
    }
    return props.isMulti ? [] : null;
  }, [props.optionsSelected, props.defaultSelected, props.selectAll, propertyOptions, props.isMulti]);

  return (
    <div className={`selector ${props.className ?? ""}`}>
      <SelectInput
        {...props}
        width={props.width}
        className={props.className}
        placeholder={props.placeholder ? props.placeholder : props.isMulti ? t("main.selectProperties") : t("main.selectProperty")}
        options={propertyOptions}
        isMulti={props.isMulti}
        value={selectedValue}
        onChange={(e) => e && changedSelected(e)}
        isDisabled={props.disabled}
      />
    </div>
  );
};

export default PropertySelector;
